import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import { Product } from '../types/product.type';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_PRODUCTS_API_PATHS } from '../constants/products-api-paths.constants';

export interface GetProductsRequest {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  filters: {
    name?: string;
    category?: string;
    subCategory?: string;
    isActive?: boolean;
    archived?: boolean;
  };
}

type UseAdminGetProductsResult = {
  isLoading: boolean;
  isRefetching: boolean;
  totalCount: number;
  products: Product[];
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetProductsResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  totalCount: 0,
  products: [],
};

export function useAdminGetProducts({
  page,
  limit,
  sortBy,
  sortDirection,
  filters,
}: GetProductsRequest): UseAdminGetProductsResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const serializedFilters = JSON.stringify(filters);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<{ totalCount: number; products: Product[] }>(
        ADMIN_PRODUCTS_API_PATHS.GET_PRODUCTS,
        { params: { page, limit, sortBy, sortDirection, filters: serializedFilters } }
      );

      setResult({
        isLoading: false,
        isRefetching: false,
        totalCount: response.totalCount,
        products: response.products,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des produits'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, totalCount: 0, products: [] });
    }
  }, [page, limit, sortBy, sortDirection, serializedFilters, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
